const EMERALD_EXCLUDED_ROUTES = ['/contact', '/packages', '/tours', '/transfers']

export function isEmeraldEligibleRoute(path) {
    return !EMERALD_EXCLUDED_ROUTES.some(route => path === route || path.startsWith(`${route}/`))
}

export function loadExternalScript({ id, src, attributes = {}, documentObject = document }) {
    const existing = documentObject.getElementById(id)

    if (existing?.dataset.loaded === 'true') {
        return Promise.resolve(existing)
    }

    return new Promise((resolve, reject) => {
        const script = existing || documentObject.createElement('script')

        function onLoad() {
            script.dataset.loaded = 'true'
            resolve(script)
        }

        function onError() {
            script.remove()
            reject(new Error(`Failed to load ${src}`))
        }

        script.addEventListener('load', onLoad, { once: true })
        script.addEventListener('error', onError, { once: true })

        if (!existing) {
            script.id = id
            script.async = true
            script.crossOrigin = 'anonymous'
            script.src = src
            Object.entries(attributes).forEach(([name, value]) => {
                script.setAttribute(name, value)
            })
            documentObject.head.appendChild(script)
        }
    })
}

function abortableDelay(delay, signal) {
    return new Promise((resolve) => {
        const timeout = window.setTimeout(resolve, delay, true)

        signal.addEventListener('abort', () => {
            window.clearTimeout(timeout)
            resolve(false)
        }, { once: true })
    })
}

// The load event waits for the eager LCP image. A short quiet window after it
// keeps Emerald from competing with the final paint on slower devices.
export async function waitForLcpWindow(signal, quietWindow = 2000) {
    if (document.readyState !== 'complete') {
        const loaded = await new Promise((resolve) => {
            const onLoad = () => resolve(true)
            const onAbort = () => {
                window.removeEventListener('load', onLoad)
                resolve(false)
            }

            window.addEventListener('load', onLoad, { once: true })
            signal.addEventListener('abort', onAbort, { once: true })
        })

        if (!loaded) {
            return false
        }
    }

    return abortableDelay(quietWindow, signal)
}

export function waitForReadingIntent(signal, { scrollThreshold = 0.1, fallbackDelay = 15000 } = {}) {
    return new Promise((resolve) => {
        const timeout = window.setTimeout(finish, fallbackDelay, true)

        function finish(result) {
            window.clearTimeout(timeout)
            window.removeEventListener('scroll', onScroll)
            resolve(result)
        }

        function onScroll() {
            const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight

            if (scrollableHeight > 0 && window.scrollY / scrollableHeight >= scrollThreshold) {
                finish(true)
            }
        }

        signal.addEventListener('abort', () => finish(false), { once: true })
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
    })
}

// openwidget.js does NOT create `window.OpenWidget`; it only hydrates an object that
// already exists (its installer is guarded by `"OpenWidget" in window`, and that branch
// is what attaches `_h` and the real `on`/`once`/`call`). The vendor's inline snippet
// normally provides this stub, and dropping it left `window.OpenWidget` undefined — so
// `once('ready')` threw and the launcher stayed stuck on its error label.
//
// `init` is deliberately absent: we inject the script through `loadExternalScript` so a
// failed load rejects instead of failing silently.
export function ensureOpenWidgetApi() {
    if (window.OpenWidget) {
        return window.OpenWidget
    }

    const api = { _q: [], _h: null, _v: '2.0' }

    // Until the bundle sets `_h`, every call is buffered in `_q` and replayed on init.
    const enqueue = entry => (api._h ? api._h.apply(null, entry) : api._q.push(entry))

    Object.assign(api, {
        on: (...args) => enqueue(['on', args]),
        once: (...args) => enqueue(['once', args]),
        off: (...args) => enqueue(['off', args]),
        get: (...args) => {
            if (!api._h) {
                throw new Error('[OpenWidget] You can\'t use getters before load.')
            }

            return enqueue(['get', args])
        },
        call: (...args) => enqueue(['call', args]),
    })

    window.OpenWidget = api

    return api
}

export function waitForOpenWidgetReady(timeoutDelay = 20000) {
    return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error('OpenWidget readiness timed out')), timeoutDelay)

        window.OpenWidget.once('ready', () => {
            window.clearTimeout(timeout)
            resolve()
        })
    })
}
